import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing Excel-like table state
 * Handles focus, editing, and row-level operations
 */
export function useExcelTableState<TRow extends { id?: number; isNew?: boolean }>() {
  const [focusedRowIndex, setFocusedRowIndex] = useState<number | null>(null);
  const [editingCell, setEditingCell] = useState<{ row: number; field: keyof TRow } | null>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const savingRef = useRef<Set<number | string>>(new Set());

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Handle row focus - called when any field in a row is focused
   * Saves the previous row if moving to a different row
   */
  const handleRowFocus = useCallback(
    (index: number, rows: TRow[], onSaveRow: (row: TRow, index: number) => void) => {
      // Clear pending blur timeout
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      // Save previous row when moving to different row
      if (focusedRowIndex !== null && focusedRowIndex !== index) {
        const previousRow = rows[focusedRowIndex];
        if (previousRow) {
          onSaveRow(previousRow, focusedRowIndex);
        }
      }

      setFocusedRowIndex(index);
    },
    [focusedRowIndex]
  );

  /**
   * Handle row blur - called when focus leaves a field
   * Uses timeout to detect if focus moved outside the table
   */
  const handleRowBlur = useCallback(
    (rows: TRow[], onSaveRow: (row: TRow, index: number) => void) => {
      blurTimeoutRef.current = setTimeout(() => {
        if (focusedRowIndex !== null) {
          const row = rows[focusedRowIndex];
          if (row) {
            onSaveRow(row, focusedRowIndex);
          }
        }
        setFocusedRowIndex(null);
      }, 150);
    },
    [focusedRowIndex]
  );

  /**
   * Check if a save operation is in progress for a specific row
   */
  const isSaving = useCallback((rowKey: number | string): boolean => {
    return savingRef.current.has(rowKey);
  }, []);

  /**
   * Mark a row as being saved (prevents duplicate saves)
   */
  const markSaving = useCallback((rowKey: number | string) => {
    savingRef.current.add(rowKey);
  }, []);

  /**
   * Mark a row as done saving
   */
  const markSaved = useCallback((rowKey: number | string) => {
    savingRef.current.delete(rowKey);
  }, []);

  return {
    focusedRowIndex,
    setFocusedRowIndex,
    editingCell,
    setEditingCell,
    handleRowFocus,
    handleRowBlur,
    isSaving,
    markSaving,
    markSaved,
  };
}
